import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_OMISE_FEE,
  LINE_ITEM_PROVIDER_COMMISSION,
  propTypes,
} from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './OrderBreakdown.module.css';
import { getAllFeeAmounts, getBookingTotalPeriod } from '../../util/genericHelpers';
import { CHARGE_STATUS, TRANSITIONS } from '../../util/enums';
import { lineItemsTotal, nonCommissionReversalLineItems } from './LineItemRefundMaybe';
import InfoPopup from '../InfoPopup/InfoPopup';

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */

const { Money } = sdkTypes;

const LineItemTotalPrice = props => {
  const {
    transaction,
    intl,
    lineItems,
    overTime,
    omiseChargeStatus,
    payinTotalAmount,
    payoutTotalAmount,
    marketplaceCurrency,
    isCustomRefund,
    isCancelTranistion,
    isCardDecline,
    allLineItems,
    isProvider,
  } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);

  const isDisputeCancel = transaction?.attributes?.transitions.some(
    t => t?.transition === TRANSITIONS.CANCEL_FROM_DISPUTED
  );
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);
  const { timeRange, totalHours } = (overTime > 0 && getBookingTotalPeriod(transaction)) || {};
  const { refundProviderCommission, refundCustomerCommission, refundCustomerOmiseFee } =
    getAllFeeAmounts(lineItems) || {};
  const refundLineItems = nonCommissionReversalLineItems(lineItems);

  const customerCommissionLineItem = lineItems?.find(
    item => item.code === LINE_ITEM_CUSTOMER_COMMISSION
  );

  const customerOmiseFeeLineItem = lineItems?.find(item => item.code === LINE_ITEM_OMISE_FEE);
  const platFormFee = Number(customerCommissionLineItem?.lineTotal?.amount || 0);
  const paymentFee = Number(customerOmiseFeeLineItem?.lineTotal?.amount || 0);

  // Check if charge status is HALF_REFUND
  const isHalfRefund = omiseChargeStatus === CHARGE_STATUS.HALF_REFUND;

  // Calculate the refund (apply half refund logic if needed)
  const refund = lineItemsTotal(refundLineItems, marketplaceCurrency, isHalfRefund);

  const listingRefund =
    refundLineItems.length > 0 ? Math.abs(refund?.amount || 0) : 0;

  const nonRefundAmount = isHalfRefund
    ? platFormFee + paymentFee + listingRefund
    : platFormFee + paymentFee;

  const formatedNonRefundAmount = new Money(
    Number(nonRefundAmount || 0),
    marketplaceCurrency
  );



  const validProviderCommissionRefundAmount =
    omiseChargeStatus === CHARGE_STATUS.HALF_REFUND
      ? refundProviderCommission / 2
      : refundProviderCommission;

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }
  const totalLabel = isProvider ? (
    omiseChargeStatus === CHARGE_STATUS.HALF_REFUND ? (
      'Total amount'
    ) : (
      <FormattedMessage id={providerTotalMessageId} />
    )
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  const overTimeLabel = (
    <FormattedMessage id="OrderBreakdown.totalHoursLabel" values={{ timeRange, totalHours }} />
  );

  const validPayout =
    omiseChargeStatus === CHARGE_STATUS.HALF_REFUND
      ? listingRefund - validProviderCommissionRefundAmount
      : payoutTotalAmount;

  const validPayin =
    omiseChargeStatus === CHARGE_STATUS.HALF_REFUND
      ? Math.abs(listingRefund) +
      Math.abs(refundCustomerCommission) +
      Math.abs(refundCustomerOmiseFee)
      : payinTotalAmount;

  const formatedPayinTotalAmount =
    payinTotalAmount > 0 && new Money(validPayin || 0, marketplaceCurrency);
  const formatedPayoutTotalAmount =
    payoutTotalAmount > 0 && new Money(validPayout || 0, marketplaceCurrency);

  const totalPrice =
    isProvider && isCustomRefund
      ? formatedPayoutTotalAmount
      : isProvider
        ? transaction.attributes.payoutTotal
        : (!isProvider &&
          !isCardDecline &&
          (omiseChargeStatus == CHARGE_STATUS.REFUNDED ||
            omiseChargeStatus == CHARGE_STATUS.HALF_REFUND)) ||
          isDisputeCancel
          ? formatedNonRefundAmount
          : isCustomRefund && !isProvider && !isCancelTranistion
            ? formatedPayinTotalAmount
            : transaction.attributes.payinTotal;
  // const formattedTotalPrice = formatMoney(intl, totalPrice);
    const formattedTotalPrice = totalPrice
    ? formatMoney(intl, totalPrice)
    : formatMoney(intl, new Money(0, marketplaceCurrency));

  const omiseFeeItem = allLineItems?.find(
    item => item.code === LINE_ITEM_OMISE_FEE && !item.reversal
  );

  const formattedOmiseFee = omiseFeeItem?.lineTotal
    ? formatMoney(intl, omiseFeeItem.lineTotal)
    : null;
  const paymentFeeTooltip = intl.formatMessage({
    id: 'OrderBreakdown.paymentFeeInfo',
  });
  return (
    <>
      {overTime > 0 ? (
        <>
          <div className={css.lineItemTotal}>
            <div className={css.totalLabel}>{overTimeLabel}</div>
          </div>
          <div className={css.lineItemTotal}>
            <div className={css.totalLabel}>{totalLabel}</div>
            <div className={css.totalPrice}>{formattedTotalPrice}</div>
          </div>
        </>
      ) : (
        <div className={css.lineItemTotal}>
          <div className={css.totalLabel}>{totalLabel}</div>
          <div className={css.totalPrice}>{formattedTotalPrice}</div>
        </div>
      )}
      {/* {isProvider ? (
        <div className={css.lineItemTotal}>
          <div className={css.paymentFeeLabel}>
            <FormattedMessage id="OrderBreakdown.paymentFeeLabel" />
            <InfoPopup info={paymentFeeTooltip} customColor='hsl(240 3% 60%)'> (?)</InfoPopup>
          </div>

          <div className={css.lineItemMinor}>{formattedOmiseFee}</div>
        </div>
      ) : null} */}
    </>
  );
};

export default LineItemTotalPrice;
